import type { AxiosRequestConfig } from 'axios';
import axios from 'axios';

import { BACKEND_API_URL } from '@/lib/public-api-url';

export type FanficsCatalogType = 'fanfic' | 'original';
export type FanficsCatalogSortBy =
  | 'popular'
  | 'newest'
  | 'best'
  | 'updated'
  | 'hotNew'
  | 'bestMonth';

export const FANFICS_CATALOG_SORT_VALUES: FanficsCatalogSortBy[] = [
  'popular',
  'newest',
  'best',
  'updated',
  'hotNew',
  'bestMonth',
];

export const DEFAULT_FANFICS_CATALOG_SORT: FanficsCatalogSortBy = 'hotNew';

interface BuildFanficsCatalogQueryParams {
  type: FanficsCatalogType;
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: FanficsCatalogSortBy;
  fandoms?: string[];
  tags?: string[];
  universes?: string[];
  statuses?: string[];
  ratings?: string[];
  characters?: string[];
  pairingCharacters?: string[];
  fandomSlug?: string;
  characterSlug?: string;
  pairingSlug?: string;
  tagSlug?: string;
  /** When true, backend injects active promo works into the listing. Catalog pages only. */
  includePromo?: boolean;
}

const FANFICS_CATALOG_API_URL = BACKEND_API_URL;

export const fanficsCatalogApi = axios.create({
  baseURL: FANFICS_CATALOG_API_URL,
});

export const fanficsCatalogSortMapping: Record<FanficsCatalogSortBy, string> = {
  popular: '-readersCount',
  newest: '-publishedAt',
  best: '-likesCount',
  updated: '-updatedAt',
  hotNew: 'hotNew',
  bestMonth: 'bestMonth',
};

export function buildFanficsCatalogQuery({
  type,
  page = 1,
  limit = 20,
  search,
  sortBy = 'popular',
  fandoms,
  tags,
  universes,
  statuses,
  ratings,
  characters,
  pairingCharacters,
  fandomSlug,
  characterSlug,
  pairingSlug,
  tagSlug,
  includePromo = false,
}: BuildFanficsCatalogQueryParams): string {
  const queryParams = new URLSearchParams();

  queryParams.set('page', page.toString());
  queryParams.set('limit', limit.toString());
  queryParams.set('type', type);
  queryParams.set('sort', fanficsCatalogSortMapping[sortBy]);

  if (includePromo) {
    queryParams.set('includePromo', '1');
  }

  if (search) {
    queryParams.set('search', search);
  }

  if (fandomSlug) {
    queryParams.set('fandomSlug', fandomSlug);
  } else if (fandoms?.length) {
    queryParams.set('fandom', fandoms.join(','));
  }

  if (characterSlug) {
    queryParams.set('characterSlug', characterSlug);
  } else if (characters?.length) {
    queryParams.set('characters', characters.join(','));
  }

  if (pairingSlug) {
    queryParams.set('pairingSlug', pairingSlug);
  } else if (pairingCharacters?.length) {
    queryParams.set('pairingCharacters', pairingCharacters.join(','));
  }

  if (tagSlug) {
    queryParams.set('tagSlug', tagSlug);
  } else if (tags?.length) {
    queryParams.set('tags', tags.join(','));
  }

  if (universes?.length) {
    queryParams.set('universe', universes.join(','));
  }

  if (statuses?.length) {
    queryParams.set('postStatus', statuses.join(','));
  }

  if (ratings?.length) {
    queryParams.set('rating', ratings.join(','));
  }

  return queryParams.toString();
}

export function createFanficsCatalogHeaders(
  accessToken?: string,
): AxiosRequestConfig['headers'] | undefined {
  if (!accessToken) {
    return undefined;
  }

  return {
    Authorization: `Bearer ${accessToken}`,
  };
}
